import type { MindmapNode } from '@/lib/mindmap-markdown';
import {
  buildWorkTree,
  layoutSide,
  sideSpan,
  splitSides,
  tagSide,
  toLaidOutNode,
  type MeasureLabel,
  type MindmapLayout,
  type WorkNode,
} from './sideTree';

/**
 * The lesson mindmap as a roadmap. The main node (the lesson or chapter title,
 * the `#` heading) sits at the top; the concepts (`##`) follow it one after
 * another down a winding path, in the order the lesson authored them,
 * alternating a little right and left of centre; and
 * each concept's sub-nodes (`###` and deeper) branch off it to the left and
 * right as side trees.
 *
 * Replaced a two-sided layout that fanned every concept out from the centre.
 * That one was bound by WIDTH: in the ~600px lesson panel even a typical
 * 19-node map opened at 0.59x zoom with 6.5px leaf text. A roadmap grows
 * DOWNWARD, so it opens readable and scrolls, and the path shows the learner
 * what to learn first.
 */

/** Vertical space between one step's block and the next. */
export const STAGE_GAP = 40;
/**
 * How far each step sits off-centre, alternating right and left, so the steps
 * do not stack in one rigid column and the path between them flows as a
 * gentle S-curve. Small on purpose: every px here widens the map and so
 * lowers the zoom it opens at.
 */
export const STEP_OFFSET = 40;

export function layoutRoadmap(
  tree: MindmapNode,
  measure: MeasureLabel,
  /** Safety cap on label length (`MAX_LABEL_CHARS` by default); labels
   *  under it wrap rather than being cut. */
  maxChars?: number
): MindmapLayout {
  const root = buildWorkTree(tree, measure, {
    id: '0',
    depth: 0,
    branch: -1,
    parentId: null,
    maxChars,
  });
  const layout: MindmapLayout = { nodes: [], edges: [] };
  layout.nodes.push(toLaidOutNode(root));

  let top = root.h / 2 + STAGE_GAP;
  let previous: WorkNode = root;
  root.children.forEach((concept, index) => {
    const { right, left } = splitSides(concept.children);
    right.forEach(b => tagSide(b, 'right'));
    left.forEach(b => tagSide(b, 'left'));
    const stage = Math.max(concept.h, sideSpan(right), sideSpan(left));
    concept.x = index % 2 === 0 ? STEP_OFFSET : -STEP_OFFSET;
    concept.y = top + stage / 2;
    const anchor = { cx: concept.x, cy: concept.y, halfWidth: concept.w / 2 };
    layoutSide(right, anchor, 1);
    layoutSide(left, anchor, -1);

    layout.edges.push({
      id: `${previous.id}=>${concept.id}`,
      source: previous.id,
      target: concept.id,
      branch: concept.branch,
      depth: concept.depth,
      side: 'right',
      kind: 'spine',
    });
    flattenStep(concept, layout);
    previous = concept;
    top += stage + STAGE_GAP;
  });
  return layout;
}

/** A concept and its sub-nodes, with parent-to-child edges. */
function flattenStep(node: WorkNode, layout: MindmapLayout) {
  layout.nodes.push(toLaidOutNode(node));
  for (const child of node.children) {
    layout.edges.push({
      id: `${node.id}->${child.id}`,
      source: node.id,
      target: child.id,
      branch: child.branch,
      depth: child.depth,
      side: child.side === 'left' ? 'left' : 'right',
      kind: 'tree',
    });
    flattenStep(child, layout);
  }
}

/**
 * What a clicked node keeps at full strength: its ancestors, its own subtree,
 * and the path down to its step - the main node and every concept up to and
 * including its own. Clicking step 3 lights the route you walk to reach it.
 */
export function relatedIds(layout: MindmapLayout, id: string): Set<string> {
  const byId = new Map(layout.nodes.map(n => [n.id, n]));
  const related = new Set<string>();
  const clicked = byId.get(id);
  if (!clicked) return related;
  for (let cur: typeof clicked | undefined = clicked; cur; ) {
    related.add(cur.id);
    cur = cur.parentId ? byId.get(cur.parentId) : undefined;
  }
  // Ids are dotted paths, so descendants share the prefix.
  for (const n of layout.nodes) {
    if (n.id.startsWith(`${id}.`)) related.add(n.id);
    if (n.depth === 1 && n.branch <= clicked.branch) related.add(n.id);
  }
  return related;
}
